import type { Catalog } from './types/catalog'
import { DefaultCatalog } from './types/catalog'
import type { ExtendedMaterial, Material } from './types/material'
import { EmptyMaterial } from './types/material'
import type { MaterialProperty } from './types/materialProperty'
import type { DirrtyScriptErrorData } from './types/substitutes'
import type { QuantityUnit } from './types/quantity'
import { SvelteMap } from 'svelte/reactivity'
import {
	type Filter,
	ListFilter,
	OrFilter,
	StringContainsFilter,
	TranslatedEnumContainsFilter
} from '$utils/filter'

/**
 * Reactive state container for the material-catalog page (MMPV).
 *
 * Replaces the six `writable()` stores that `MaterialCatalogPageManager`
 * previously created and shared through individually-keyed contexts
 * (`selectedCatalog`, `selectedMaterial`, `extended-materials`,
 * `materialProperties`, `materialPropertyParents`, `script-errors`).
 * Every consumer now imports the shared `mmpvState` singleton below and
 * reads/writes it by property access — no `setContext`/`getContext` plumbing.
 *
 * Map/array fields are `$state` proxies, so in-place mutation
 * (`materialProperties.set(...)`, `allExtendedMaterials.push(...)`) is reactive —
 * the old `$store = $store` reassignment hacks are no longer needed.
 *
 * NOTE: `formErrors` and `useMaterialDesign` are intentionally NOT here. They are
 * cross-feature contexts also provided by map/fak wrappers (FakView, FakBaseLayout,
 * Minilot) and remain standalone `writable`/boolean contexts.
 */
class MmpvState {
	/**
	 * placeholder `DefaultCatalog()` (not `null`) preserves the original
	 * non-null `Writable<Catalog>` contract — the manager overwrites it in
	 * `onMount` before any child reads `.nodes` / `.name`, so consumers never
	 * need null guards.
	 */
	selectedCatalog: Catalog = $state(DefaultCatalog())
	selectedMaterial: Material = $state(EmptyMaterial())
	// each material includes full code and catalog name, used for substitutes
	allExtendedMaterials: ExtendedMaterial[] = $state([])
	materialProperties: Map<string, MaterialProperty> = new SvelteMap()
	materialPropertyParents: Map<string, string[]> = new SvelteMap()
	scriptErrors: DirrtyScriptErrorData[] | null = $state(null)
	catalogs: Catalog[] = $state([])
	quantities: QuantityUnit[] = $state([])

	/* filters and search term **/
	filters: Filter[] = $state([])
	searchTerm: string = $state('')

	/* tree expansion states of opened and closed nodes in the catalogs and materials */
	treeExpansionStates: Map<string, string[]> = new SvelteMap()

	findMaterial(materialId: string) {
		return this.selectedCatalog.nodes.find((parentNode: Material) => parentNode.id === materialId)
	}

	updateMaterialOnQuantity(quantityId: string | null | undefined, materialId: string | undefined) {
		if (quantityId === undefined || !materialId) {
			return
		}

		// in case the quantity has been removed from the material, the quantityId is null -> remove material from all lists
		if (quantityId === null) {
			this.quantities = this.quantities.map((q) => {
				q.materialNodeIds = q.materialNodeIds?.filter(
					(materialNodeId) => materialNodeId !== materialId
				)
				return q
			})
			return
		}

		/**
		 * target quantity isn't loaded locally — leave existing assignments untouched rather
		 * than stripping the material from its old quantity with nowhere to put it
		 */
		if (!this.quantities.some((q) => q.publicId === quantityId)) {
			return
		}

		this.quantities = this.quantities.map((quantity) => {
			const currentIds = quantity.materialNodeIds ?? []
			const filteredIds = currentIds.filter((id) => id !== materialId)

			if (quantity.publicId === quantityId) {
				return { ...quantity, materialNodeIds: [...filteredIds, materialId] }
			}

			return filteredIds.length === currentIds.length
				? quantity
				: { ...quantity, materialNodeIds: filteredIds }
		})
	}

	getMaterialProperty(id: string | undefined | null) {
		if (!id) return null

		return this.materialProperties.get(id)
	}

	deleteMaterialProperty(propertyId: string | undefined | null) {
		if (!propertyId) return

		this.materialProperties.delete(propertyId)
		this.materialPropertyParents.delete(propertyId)
	}

	expandTreeNode(key: string, nodeId: string) {
		const existing = this.treeExpansionStates.get(key) ?? []
		if (existing.includes(nodeId)) return
		this.treeExpansionStates.set(key, [...existing, nodeId])
	}

	getParentsNamesAsString(propertyId: string): string | undefined {
		return this.materialPropertyParents.has(propertyId)
			? this.materialPropertyParents
					.get(propertyId)
					?.map((id: string) => this.getMaterialProperty(id)?.name)
					?.join(',')
			: ''
	}

	/**
	 * Restore every field to its initial value.
	 *
	 * The singleton lives for the whole module lifetime, so its state would
	 * otherwise leak between Storybook stories and Vitest cases that share the
	 * same session. Story/test wrappers call `reset()` before seeding to get the
	 * fresh-instance isolation the old per-subtree context used to provide.
	 */
	reset() {
		this.selectedCatalog = DefaultCatalog()
		this.selectedMaterial = EmptyMaterial()
		this.allExtendedMaterials = []
		this.materialProperties.clear()
		this.materialPropertyParents.clear()
		this.scriptErrors = null
		this.catalogs = []
		this.quantities = []
		this.treeExpansionStates.clear()

		this.resetSearchAndFilters()
	}

	searchCatalogs() {
		this.filters.push(new StringContainsFilter('name', this.searchTerm))
	}

	searchMaterials() {
		this.filters.push(
			new OrFilter([
				new StringContainsFilter('name', this.searchTerm),
				new StringContainsFilter('code', this.searchTerm)
			])
		)
	}

	searchProperties() {
		this.filters.push(
			new OrFilter([
				new StringContainsFilter('name', this.searchTerm),
				new ListFilter(
					new TranslatedEnumContainsFilter('flags', this.searchTerm, 'PropertySystemFlag.')
				)
			])
		)
	}

	searchQuantities() {
		this.filters.push(
			new OrFilter([
				new StringContainsFilter('name', this.searchTerm),
				new StringContainsFilter('label', this.searchTerm),
				new StringContainsFilter('shortLabel', this.searchTerm),
				new StringContainsFilter('unit', this.searchTerm)
			])
		)
	}

	resetSearchAndFilters() {
		this.searchTerm = ''
		this.filters = []
	}
}

/** Shared MMPV page state. Import and use directly; do not construct your own. */
export const mmpvState = new MmpvState()
